import { setup, assign, assertEvent, fromCallback, stopChild, type ActorRefFrom } from "xstate";
import { atomWithMachine } from "jotai-xstate";
import type { Artwork } from "@design-lab/entities/artwork";
import type { models } from "jiffy-api";
import { transferArtworkMachine } from "./transfer-artwork.machine";

export type TransfersTab = "upload" | "review" | "sizes";

export type TransferArtworkActor = ActorRefFrom<typeof transferArtworkMachine>;

export type TransfersManagerContext = {
  items: TransferArtworkActor[];
  selectedId: string | null;
  artworkIdToActorId: Record<number, string>;
  hasUploadingArtwork: boolean;
  uploadingCount: number;
  currentTab: TransfersTab;
  allArtworksReviewed: boolean;
  allArtworksHaveQuantity: boolean;
};

const transfersManagerMachineDefinition = setup({
  types: {
    context: {} as TransfersManagerContext,
    events: {} as
      | {
          type: "artwork.add";
          file?: File;
          artworkId?: number;
          artwork?: any;
          source: "upload" | "design_library" | "canva" | "ai_generation";
        }
      | { type: "artwork.remove"; id: string }
      | { type: "artwork.select"; id: string | null }
      | { type: "artworks.clear" }
      | { type: "artwork.upload.completed"; id: string; artworkId: number }
      | { type: "websocket.message"; message: any }
      | { type: "check.uploading" }
      | { type: "tab.change"; tab: TransfersTab }
      | { type: "tab.next" }
      | { type: "review.state.changed" }
      | { type: "quantity.state.changed" }
  },
  actors: {
    transferArtwork: transferArtworkMachine,
    tick: fromCallback(({ sendBack }) => {
      const id = setInterval(() => sendBack({ type: "__tick" as any }), 60_000);
      return () => clearInterval(id);
    })
    // Removed uploadingChecker - was causing re-renders every 100ms
    // Now using event-driven approach: uploading state updates only when relevant events occur
  },
  actions: {
    updateUploadingState: assign(({ context }) => {
      const uploadingItems = context.items.filter(item => {
        const snapshot = item.getSnapshot();
        return snapshot.hasTag && snapshot.hasTag("loading");
      });
      return {
        hasUploadingArtwork: uploadingItems.length > 0,
        uploadingCount: uploadingItems.length
      };
    }),
    addArtwork: assign(({ context, event, spawn }) => {
      assertEvent(event, "artwork.add");
      const id = `artwork-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const actor = spawn("transferArtwork", { id, systemId: id, input: {} });

      if (event.source === "design_library" && event.artworkId && event.artwork) {
        // For library items, skip the upload process and go directly to finished
        // Set the context with the artwork data
        actor.send({
          type: "reset",
          context: {
            artworkId: event.artworkId,
            artwork: event.artwork,
            source: event.source,
            visitedStates: ["finished"],
            cacheWarmingStatus: "none",
            isReviewed: false,
            quantity: 0
          },
          targetState: "finished"
        });
      } else if (event.file) {
        // For file uploads, start the upload process
        actor.send({ type: "upload.started", file: event.file, source: event.source });
      }

      const newItems = [...context.items, actor];
      // Check if any items are uploading
      const hasUploadingArtwork = event.file ? true : context.hasUploadingArtwork;
      const uploadingCount = event.file ? context.uploadingCount + 1 : context.uploadingCount;

      return {
        items: newItems,
        selectedId: context.selectedId ?? id,
        hasUploadingArtwork,
        uploadingCount
      };
    }),
    removeArtwork: assign(({ context, event }) => {
      assertEvent(event, "artwork.remove");
      const remaining = context.items.filter(a => a.id !== event.id);
      const nextSelected =
        context.selectedId === event.id ? (remaining[0]?.id ?? null) : context.selectedId;

      // Check if any remaining items are uploading
      const uploadingItems = remaining.filter(item => {
        const snapshot = item.getSnapshot();
        return snapshot.hasTag && snapshot.hasTag("loading");
      });

      return {
        items: remaining,
        selectedId: nextSelected,
        hasUploadingArtwork: uploadingItems.length > 0,
        uploadingCount: uploadingItems.length
      };
    }),
    selectArtwork: assign(({ event }) => {
      assertEvent(event, "artwork.select");
      return { selectedId: event.id };
    }),
    clearAll: assign(() => ({
      items: [],
      selectedId: null,
      artworkIdToActorId: {},
      hasUploadingArtwork: false,
      uploadingCount: 0
    })),
    onUploadCompleted: assign(({ context, event }) => {
      assertEvent(event, "artwork.upload.completed");
      const nextMap = { ...context.artworkIdToActorId, [event.artworkId]: event.id };
      const target = context.items.find(a => a.id === event.id);
      if (target) {
        target.send({ type: "upload.completed", id: event.artworkId });
      }
      return { artworkIdToActorId: nextMap };
    }),
    handleWebsocket: ({ context, event }) => {
      assertEvent(event, "websocket.message");
      const { message } = event as { message: models.cable.DtfChannel.Actions };
      if (!message || !("result" in message)) return;
      const artworkId = message.result?.artworkId;
      if (!artworkId) return;
      const actorId = context.artworkIdToActorId[artworkId];
      const target = actorId ? context.items.find(a => a.id === actorId) : undefined;
      if (!target) return;

      const { action } = message;
      if (action === "process_dtf_artwork_started") {
        target.send({ type: "enhancement.started" });
      } else if (action === "fetch_delivery_date") {
        const art =
          (message as { data?: { artwork?: Artwork }; result?: { artwork?: Artwork } }).data
            ?.artwork ??
          (message as { data?: { artwork?: Artwork }; result?: { artwork?: Artwork } }).result
            ?.artwork;
        if (art) target.send({ type: "enhancement.completed", artwork: art });
      } else if (action === "validate_artwork") {
        const art =
          (message as { data?: { artwork?: Artwork } }).data?.artwork ??
          ((message as { data?: Artwork }).data as Artwork | undefined);
        if (art) target.send({ type: "enhancement.completed", artwork: art });
      }
      target.send({ type: "websocket.event", name: message.action });
    },
    validateAndSwitchTab: assign(({ context, event }) => {
      assertEvent(event, "tab.change");
      const { tab } = event;
      const { hasUploadingArtwork, items } = context;
      const hasArtworks = items.length > 0;

      // Calculate allArtworksReviewed on-demand
      const allArtworksReviewed =
        items.length > 0 &&
        items.every(item => {
          const snapshot = item.getSnapshot();
          return snapshot.context.isReviewed;
        });

      // Calculate allArtworksHaveQuantity on-demand
      const allArtworksHaveQuantity =
        items.length > 0 &&
        items.every(item => {
          const snapshot = item.getSnapshot();
          return snapshot.context.quantity > 0;
        });

      // Prevent switching to review/sizes if artworks are loading
      if (hasUploadingArtwork && (tab === "review" || tab === "sizes")) {
        return {}; // Don't change tab
      }

      // Prevent switching to sizes if no artworks or not all reviewed
      if (tab === "sizes" && (!hasArtworks || !allArtworksReviewed)) {
        return {}; // Don't change tab
      }

      // Update tab and both state flags
      return {
        currentTab: tab,
        allArtworksReviewed,
        allArtworksHaveQuantity
      };
    }),
    autoSwitchTab: assign(({ context }) => {
      const { currentTab, hasUploadingArtwork, items } = context;
      const hasArtworks = items.length > 0;

      // Calculate allArtworksReviewed on-demand
      const allArtworksReviewed =
        items.length > 0 &&
        items.every(item => {
          const snapshot = item.getSnapshot();
          return snapshot.context.isReviewed;
        });

      // Calculate allArtworksHaveQuantity on-demand
      const allArtworksHaveQuantity =
        items.length > 0 &&
        items.every(item => {
          const snapshot = item.getSnapshot();
          return snapshot.context.quantity > 0;
        });

      // Auto-switch from review/sizes if artworks are loading
      if (hasUploadingArtwork && (currentTab === "review" || currentTab === "sizes")) {
        return {
          currentTab: "upload" as TransfersTab,
          allArtworksReviewed,
          allArtworksHaveQuantity
        };
      }

      // Auto-switch from sizes if no artworks or not all reviewed
      if (currentTab === "sizes" && (!hasArtworks || !allArtworksReviewed)) {
        return {
          currentTab: "upload" as TransfersTab,
          allArtworksReviewed,
          allArtworksHaveQuantity
        };
      }

      return { allArtworksReviewed, allArtworksHaveQuantity }; // Always update both states
    })
  }
}).createMachine({
  id: "transfersManager",
  context: () => ({
    items: [],
    selectedId: null,
    artworkIdToActorId: {},
    hasUploadingArtwork: false,
    uploadingCount: 0,
    currentTab: "upload" as TransfersTab,
    allArtworksReviewed: false,
    allArtworksHaveQuantity: false
  }),
  initial: "idle",
  states: {
    idle: {
      invoke: [{ src: "tick" }],
      on: {
        "artwork.add": {
          actions: ["addArtwork", "updateUploadingState", "autoSwitchTab"]
        },
        "artwork.remove": {
          actions: [
            "removeArtwork",
            stopChild(({ event }) => event.id),
            "updateUploadingState",
            "autoSwitchTab"
          ]
        },
        "artwork.select": { actions: "selectArtwork" },
        "artworks.clear": {
          actions: [
            ({ context }) => {
              context.items.forEach(() => {});
            },
            "clearAll",
            "updateUploadingState",
            "autoSwitchTab"
          ]
        },
        "artwork.upload.completed": {
          actions: ["onUploadCompleted", "updateUploadingState", "autoSwitchTab"]
        },
        "websocket.message": {
          actions: ["handleWebsocket", "updateUploadingState", "autoSwitchTab"]
        },
        "tab.change": {
          actions: "validateAndSwitchTab"
        },
        "tab.next": {
          actions: ({ context, self }) => {
            const tabOrder: TransfersTab[] = ["upload", "review", "sizes"];
            const currentIndex = tabOrder.indexOf(context.currentTab);
            const nextTab = currentIndex < tabOrder.length - 1 ? tabOrder[currentIndex + 1] : null;

            if (nextTab) {
              // Send tab.change event to reuse existing validation logic
              self.send({ type: "tab.change", tab: nextTab });
            }
          }
        },
        "review.state.changed": {
          actions: "autoSwitchTab"
        },
        "quantity.state.changed": {
          actions: "autoSwitchTab"
        }
      }
    }
  }
});

export const transfersManagerMachine = {
  machine: transfersManagerMachineDefinition,
  selectors: {
    tabs: (state: any) => {
      const {
        items,
        hasUploadingArtwork,
        allArtworksReviewed,
        allArtworksHaveQuantity,
        currentTab
      } = state.context;
      const hasArtworks = items.length > 0;

      // Upload tab is checked when there are artworks and none are uploading
      const uploadChecked = hasArtworks && !hasUploadingArtwork;

      // Image review tab is checked when all artworks are reviewed
      const reviewChecked = hasArtworks && allArtworksReviewed;

      // Sizes tab is checked when all artworks have quantity > 0
      const sizesChecked = hasArtworks && allArtworksHaveQuantity;

      return {
        currentTab,
        tabs: [
          {
            title: "Upload",
            mobileTitle: "Upload",
            value: "upload" as const,
            enabled: true,
            icon: "CloudUpload",
            checked: uploadChecked
          },
          {
            title: "Image review",
            mobileTitle: "Image review",
            value: "review" as const,
            enabled: hasArtworks && !hasUploadingArtwork,
            icon: "ScanEye",
            checked: reviewChecked
          },
          {
            title: "Select Sizes",
            mobileTitle: "Sizes",
            value: "sizes" as const,
            enabled: hasArtworks && !hasUploadingArtwork && allArtworksReviewed,
            icon: "Ruler",
            checked: sizesChecked
          }
        ]
      };
    },
    nextTabInfo: (state: any) => {
      const { currentTab } = state.context;
      const tabsData = transfersManagerMachine.selectors.tabs(state);
      const { tabs } = tabsData;

      const tabOrder: TransfersTab[] = ["upload", "review", "sizes"];
      const currentIndex = tabOrder.indexOf(currentTab);
      const nextTab = currentIndex < tabOrder.length - 1 ? tabOrder[currentIndex + 1] : null;

      if (!nextTab) return { hasNext: false, enabled: false, nextTab: null };

      const nextTabData = tabs.find((t: any) => t.value === nextTab);
      return {
        hasNext: true,
        enabled: nextTabData?.enabled || false,
        nextTab
      };
    }
  }
};

export const transfersManagerAtom = atomWithMachine(() => transfersManagerMachine.machine);
